import classNames from 'classnames'
import { useState } from 'react'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import { v4 } from 'uuid'

import Icon from '@/components/Icon/Icon'
import { useTheme } from '@/components/ThemeProvider/ThemeProvider'
import { update } from '@/store/cardlists/actions/update'
import { DEFAULT_FILTER_VALUE, DEFAULT_FILTER_VALUE_MAP, FilterType, NAME_MAP } from '@/types/additionalFilters'
import { groupByModes, sortByModes } from '@/utils/constants'
import { defaultGroupedBy, defaultSortedBy } from '@/utils/constants'

import AdditionalFilters from '../AdditionalFilters/AdditionalFilters'

import styles from './ControlsMobile.module.scss'

const handleGroupByChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  const key = groupByModes.find(({ value }) => value === event.target.value)?.key || defaultGroupedBy
  update({ groupedBy: key })
}

const handleSortByChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
  const key = sortByModes.find(({ value }) => value === event.target.value)?.key || defaultSortedBy
  update({ sortedBy: key })
}

type Props = {
  disabledSortMethods?: string[]
  filters: FilterType[]
  groupedBy?: string
  persist: boolean
  setFilters: (filters: FilterType[]) => void
  sortedBy?: string
  togglePersist: () => void
}

const ControlsMobile = ({
  disabledSortMethods,
  filters,
  groupedBy,
  persist,
  setFilters,
  sortedBy,
  togglePersist,
}: Props) => {
  const [showModal, setShowModal] = useState(false)
  const [theme] = useTheme()

  const changeFilter = (filter: keyof typeof DEFAULT_FILTER_VALUE_MAP, i: number) => {
    const newValues = DEFAULT_FILTER_VALUE_MAP[filter] || DEFAULT_FILTER_VALUE
    setFilters([
      ...filters.slice(0, i),
      {
        key: v4(),
        type: filter,
        values: newValues,
      },
      ...filters.slice(i + 1),
    ])
  }
  const handleFiltersChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const key = event.target.value
    changeFilter(key as keyof typeof DEFAULT_FILTER_VALUE_MAP, filters.length + 1)
  }

  return (
    <>
      <Button variant={theme}>
        <Icon icon='arrowDownWideShort' onClick={() => setShowModal(true)} size='lg' />
      </Button>
      <Modal className='mt-4' onHide={() => setShowModal(false)} show={showModal}>
        <Modal.Body className='pb-3'>
          <div className={classNames(styles.lock, { [styles.locked]: persist })}>
            <Button onClick={togglePersist} variant={theme}>
              <Icon icon={persist ? 'lock' : 'unlock'} />
            </Button>
          </div>
          <Form>
            <Form.Group className='mb-2'>
              <Form.Label>
                <Icon icon='clone' />
                &nbsp;Group by
              </Form.Label>
              <Form.Select
                onChange={handleGroupByChange}
                value={groupByModes.find(({ key }) => key === groupedBy)?.value || 'Category'}
              >
                {groupByModes.map(({ key, value }) => (
                  <option key={key}>{value}</option>
                ))}
                <option>Reset</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className='mb-2'>
              <Form.Label>
                <Icon icon='arrowDownWideShort' />
                &nbsp;Sort by
              </Form.Label>
              <Form.Select
                onChange={handleSortByChange}
                value={sortByModes.find(({ key }) => key === sortedBy)?.value || 'Default'}
              >
                {sortByModes
                  .filter(({ key }) => key !== 'default' && !disabledSortMethods?.includes(key))
                  .map(({ key, value }) => (
                    <option key={key}>{value}</option>
                  ))}
                <option>Reset</option>
              </Form.Select>
            </Form.Group>
            <Form.Group>
              <Form.Label>
                <Icon icon='filter' />
                &nbsp;Card Filters
              </Form.Label>
              <Form.Select onChange={handleFiltersChange} value=''>
                <option />
                {[
                  'cardName',
                  'typeLine',
                  'colors',
                  'manaValue',
                  'power',
                  'toughness',
                  'rarity',
                  'universesBeyond',
                  'sets',
                  'tags',
                  'price',
                  'salt',
                  'inclusion',
                  'num_decks',
                  'lift',
                  'synergy',
                ].map((value) => (
                  <option key={value} value={value}>
                    {NAME_MAP[value as keyof typeof NAME_MAP]}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Form>
          {filters.length > 0 && (
            <div className='mt-2'>
              <AdditionalFilters filters={filters} onSubmitFinish={() => setShowModal(false)} setFilters={setFilters} />
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default ControlsMobile
